// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { LanguageId } from '@shared/ui-kit/providers/translation-provider/i18next-config';
import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SvgIconName } from '@shared/ui-kit/components/app-icon';
/**
 * Returns a set of values that can be used to create a language selector component.
 *
 * @returns An object with the following properties:
 *
 *   - `localesIds`: An array of objects containing the language ID, translation key for
 *       the language label, and icon name.
 *   - `onChangeLanguage`: A callback function that takes the language ID as an argument
 *       and updates the language in the i18next instance.
 *   - `language`: The current language ID.
 *   - `open`: Whether the language menu is open.
 *   - `handleMenu`: A callback function to open the language menu.
 *   - `handleClose`: A callback function to close the language menu.
 *   - `anchorEl`: The DOM element that the language menu is anchored to.
 *   - `currentLocale`: The current locale object.
 */
export const useLanguageSelector = () => {
  const { i18n, t } = useTranslation();

  const localesIds = useMemo<
    Array<{
      id: LanguageId;
      value: string;
      label: string;
      icon: SvgIconName;
    }>
  >(
    () => [
      {
        id: 'en',
        value: t('auth.welcome.language.en.code', { ns: 'auth' }),
        label: t('auth.welcome.language.en.label', { ns: 'auth' }),
        icon: 'USAFlag',
      },
      {
        id: 'es',
        value: t('auth.welcome.language.es.code', { ns: 'auth' }),
        label: t('auth.welcome.language.es.label', { ns: 'auth' }),
        icon: 'SpainFlag',
      },
    ],
    [t],
  );

  const language = useMemo(() => i18n.language as LanguageId, [i18n.language]);

  const currentLocale = useMemo(
    () => localesIds.find(locale => locale.id === language)!,
    [language, localesIds],
  );

  const onChangeLanguage = useCallback(
    (language: LanguageId) => {
      i18n.changeLanguage(language);
    },
    [i18n],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const handleMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return {
    localesIds,
    onChangeLanguage,
    language,
    open,
    handleMenu,
    handleClose,
    anchorEl,
    currentLocale,
  } as const;
};
